import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Users, Package, Files, BookOpen, Question, FilePdf, Folder, Gear, ChartBar, Globe, Warning, Ruler, SignOut, ChartLineUp, CalendarCheck, Table, Truck, Flag, Tag } from "@phosphor-icons/react";
import AlertsBell from "@/components/AlertsPanel";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/contexts/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { t } = useSettings();
  const [pendingComplaintsCount, setPendingComplaintsCount] = useState(0);

  useEffect(() => {
    axios.get(`${API}/complaints`, { params: { status: 'pendente' } })
      .then((res) => setPendingComplaintsCount((res.data || []).length))
      .catch(() => setPendingComplaintsCount(0));
  }, []);

  return (
    <div className="animate-fade-in" data-testid="dashboard">
      {/* Outer wrapper allows the mascot to overflow past the hero box's bottom edge */}
      <div className="relative">
      {/* Hero Section with Background - Reduced height to fit screen without scroll */}
      <div 
        className="relative min-h-[54vh] lg:min-h-[58vh] flex items-center justify-center rounded-2xl overflow-hidden"
        style={{
          background: `linear-gradient(135deg, rgba(0, 147, 146, 0.85) 0%, rgba(0, 122, 121, 0.8) 100%), 
                       url('https://customer-assets.emergentagent.com/job_client-products/artifacts/5o8uy66r_image.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Help Button - Top Left */}
        <div className="absolute top-4 left-4 z-20 flex gap-2">
          <Link 
            to="/guide" 
            data-testid="btn-help-guide"
          >
            <Button 
              variant="ghost" 
              className="bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 rounded-full p-2 sm:p-3 h-auto"
            >
              <Question size={20} weight="bold" className="sm:hidden" />
              <Question size={24} weight="bold" className="hidden sm:block" />
            </Button>
          </Link>
        </div>

        {/* User Profile Button - Top Right */}
        <div className="absolute top-4 right-4 z-20 flex gap-2">
          {/* Logout Button - always shown: reaching this page already requires a real session
              (ProtectedRoute), so there is no legitimate "Entrar" state to show here anymore. */}
          <Button
            variant="ghost"
            onClick={() => document.querySelector('[data-testid="btn-header-logout-trigger"]')?.click()}
            className="bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 rounded-full p-2 sm:p-3 h-auto"
            data-testid="btn-dashboard-logout"
          >
            <SignOut size={20} weight="bold" className="sm:hidden" />
            <SignOut size={24} weight="bold" className="hidden sm:block" />
          </Button>
          {/* Alerts Bell - third circle in the row, between login/logout and settings */}
          <AlertsBell />
          {/* Profile Button */}
          <Link 
            to="/profile" 
            data-testid="btn-profile"
          >
            <Button 
              variant="ghost" 
              className="bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 rounded-full p-2 sm:p-3 h-auto"
            >
              <Gear size={20} weight="bold" className="sm:hidden" />
              <Gear size={24} weight="bold" className="hidden sm:block" />
            </Button>
          </Link>
        </div>

        {/* MOBILE LAYOUT - 2 buttons per row */}
        <div className="relative z-10 px-3 w-full pt-16 pb-4 sm:hidden">
          <h1 className="text-lg font-bold font-[Manrope] tracking-tight text-white mb-3 drop-shadow-lg text-center">
            {t('dashboard_hero_title')}
          </h1>
          <div className="grid grid-cols-2 gap-2">
            <Link to="/clients"><Button className="w-full px-2 py-2.5 text-xs bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30 rounded-lg font-semibold flex items-center justify-start gap-2 h-auto" data-testid="btn-view-clients-mobile"><Users size={16} weight="duotone" /><span>{t('dashboard_clients')}</span></Button></Link>
            <Link to="/promocao"><Button className="w-full px-2 py-2.5 text-xs bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30 rounded-lg font-semibold flex items-center justify-start gap-2 h-auto"><Ruler size={16} weight="duotone" /><span>{t('nav_promocao')}</span></Button></Link>
            <Link to="/agenda"><Button className="w-full px-2 py-2.5 text-xs bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30 rounded-lg font-semibold flex items-center justify-start gap-2 h-auto" data-testid="btn-view-agenda-mobile"><CalendarCheck size={16} weight="duotone" /><span>{t('nav_agenda')}</span></Button></Link>
            <Link to="/catalog"><Button className="w-full px-2 py-2.5 text-xs bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30 rounded-lg font-semibold flex items-center justify-start gap-2 h-auto"><Package size={16} weight="duotone" /><span>{t('dashboard_ppm')}</span></Button></Link>
            <Link to="/physical-catalogs"><Button className="w-full px-2 py-2.5 text-xs bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30 rounded-lg font-semibold flex items-center justify-start gap-2 h-auto"><BookOpen size={16} weight="duotone" /><span>{t('dashboard_catalogs')}</span></Button></Link>
            <Link to="/projetos"><Button className="w-full px-2 py-2.5 text-xs bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30 rounded-lg font-semibold flex items-center justify-start gap-2 h-auto"><Folder size={16} weight="duotone" /><span>{t('nav_projetos')}</span></Button></Link>
            <Link to="/incidencias"><Button className="w-full px-2 py-2.5 text-xs bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30 rounded-lg font-semibold flex items-center justify-start gap-2 h-auto"><Warning size={16} weight="duotone" /><span>{t('nav_incidencias')}</span></Button></Link>
            <Link to="/reclamacoes" className="relative"><Button className="w-full px-2 py-2.5 text-xs bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30 rounded-lg font-semibold flex items-center justify-start gap-2 h-auto" data-testid="btn-reclamacoes-mobile"><Flag size={16} weight="duotone" /><span>{t('nav_reclamacoes')}</span></Button>
              {pendingComplaintsCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full h-4.5 min-w-[18px] px-1 flex items-center justify-center border-2 border-white" data-testid="badge-reclamacoes-pendentes-mobile">
                  {pendingComplaintsCount}
                </span>
              )}
            </Link>
            <Link to="/catalogos"><Button className="w-full px-2 py-2.5 text-xs bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30 rounded-lg font-semibold flex items-center justify-start gap-2 h-auto" data-testid="btn-catalog-gresart-mobile"><FilePdf size={16} weight="duotone" /><span>{t('dashboard_gresart_catalog')}</span></Button></Link>
            <Link to="/documents"><Button className="w-full px-2 py-2.5 text-xs bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30 rounded-lg font-semibold flex items-center justify-start gap-2 h-auto"><Files size={16} weight="duotone" /><span>{t('nav_catalog_merch')}</span></Button></Link>
            <Link to="/precos"><Button className="w-full px-2 py-2.5 text-xs bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30 rounded-lg font-semibold flex items-center justify-start gap-2 h-auto" data-testid="btn-precos-mobile"><Tag size={16} weight="duotone" /><span>{t('nav_precos')}</span></Button></Link>
            <Link to="/tabelas"><Button className="w-full px-2 py-2.5 text-xs bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30 rounded-lg font-semibold flex items-center justify-start gap-2 h-auto" data-testid="btn-tabelas-mobile"><Table size={16} weight="duotone" /><span>{t('nav_tabelas')}</span></Button></Link>
            <Button onClick={() => window.open('https://www.gresart.com', '_blank')} className="w-full px-2 py-2.5 text-xs bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30 rounded-lg font-semibold flex items-center justify-start gap-2 h-auto"><Globe size={16} weight="duotone" /><span>gresart.com</span></Button>
            <Link to="/sales-analysis"><Button className="w-full px-2 py-2.5 text-xs bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30 rounded-lg font-semibold flex items-center justify-start gap-2 h-auto"><ChartLineUp size={16} weight="duotone" /><span>{t('dashboard_sales_analysis')}</span></Button></Link>
            <Link to="/statistics"><Button className="w-full px-2 py-2.5 text-xs bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30 rounded-lg font-semibold flex items-center justify-start gap-2 h-auto" data-testid="btn-statistics-mobile"><ChartBar size={16} weight="duotone" /><span>{t('dashboard_statistics_ppm')}</span></Button></Link>
            <Link to="/encomendas"><Button className="w-full px-2 py-2.5 text-xs bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30 rounded-lg font-semibold flex items-center justify-start gap-2 h-auto" data-testid="btn-encomendas-mobile"><Truck size={16} weight="duotone" /><span>Encomendas Pendentes</span></Button></Link>
          </div>
        </div>

        {/* DESKTOP LAYOUT - Hidden on mobile, original layout */}
        <div className="relative z-10 text-center px-4 sm:px-6 max-w-4xl mx-auto hidden sm:block">
          {/* Title */}
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-[Manrope] tracking-tight text-white mb-4 drop-shadow-lg">
            {t('dashboard_hero_title')}
          </h1>

          {/* Action Buttons - multi-column on larger screens */}
          <div className="space-y-2.5">
          <div className="grid grid-cols-4 lg:grid-cols-8 gap-2.5 justify-center">
            <Link to="/clients">
              <Button 
                className="w-full min-h-[68px] px-3 py-3 text-sm bg-white/20 backdrop-blur-sm text-white border-2 border-white/40 hover:bg-white/30 rounded-xl transition-all duration-200 font-semibold flex flex-col items-center gap-1 h-auto" 
                data-testid="btn-view-clients-desktop"
              >
                <Users size={20} weight="duotone" />
                <span>{t('dashboard_clients')}</span>
              </Button>
            </Link>
            <Link to="/promocao">
              <Button 
                className="w-full min-h-[68px] px-3 py-3 text-sm bg-white/20 backdrop-blur-sm text-white border-2 border-white/40 hover:bg-white/30 rounded-xl transition-all duration-200 font-semibold flex flex-col items-center gap-1 h-auto" 
                data-testid="btn-promocao-desktop"
              >
                <Ruler size={20} weight="duotone" />
                <span>{t('nav_promocao')}</span>
              </Button>
            </Link>
            <Link to="/agenda">
              <Button 
                className="w-full min-h-[68px] px-3 py-3 text-sm bg-white/20 backdrop-blur-sm text-white border-2 border-white/40 hover:bg-white/30 rounded-xl transition-all duration-200 font-semibold flex flex-col items-center gap-1 h-auto" 
                data-testid="btn-view-agenda-desktop"
              >
                <CalendarCheck size={20} weight="duotone" />
                <span>{t('nav_agenda')}</span>
              </Button>
            </Link>
            <Link to="/catalog">
              <Button 
                className="w-full min-h-[68px] px-3 py-3 text-sm bg-white/20 backdrop-blur-sm text-white border-2 border-white/40 hover:bg-white/30 rounded-xl transition-all duration-200 font-semibold flex flex-col items-center gap-1 h-auto" 
                data-testid="btn-view-catalog-desktop"
              >
                <Package size={20} weight="duotone" />
                <span>{t('dashboard_ppm')}</span>
              </Button>
            </Link>
            <Link to="/physical-catalogs">
              <Button 
                className="w-full min-h-[68px] px-3 py-3 text-sm bg-white/20 backdrop-blur-sm text-white border-2 border-white/40 hover:bg-white/30 rounded-xl transition-all duration-200 font-semibold flex flex-col items-center gap-1 h-auto" 
                data-testid="btn-view-physical-catalogs-desktop"
              >
                <BookOpen size={20} weight="duotone" />
                <span>{t('dashboard_catalogs')}</span>
              </Button>
            </Link>
            <Link to="/projetos">
              <Button 
                className="w-full min-h-[68px] px-3 py-3 text-sm bg-white/20 backdrop-blur-sm text-white border-2 border-white/40 hover:bg-white/30 rounded-xl transition-all duration-200 font-semibold flex flex-col items-center gap-1 h-auto" 
                data-testid="btn-projetos-desktop"
              >
                <Folder size={20} weight="duotone" />
                <span>{t('nav_projetos')}</span>
              </Button>
            </Link>
            <Link to="/incidencias">
              <Button 
                className="w-full min-h-[68px] px-3 py-3 text-sm bg-white/20 backdrop-blur-sm text-white border-2 border-white/40 hover:bg-white/30 rounded-xl transition-all duration-200 font-semibold flex flex-col items-center gap-1 h-auto" 
                data-testid="btn-incidencias-desktop"
              >
                <Warning size={20} weight="duotone" />
                <span>{t('nav_incidencias')}</span>
              </Button>
            </Link>
            <Link to="/reclamacoes" className="relative">
              <Button 
                className="w-full min-h-[68px] px-3 py-3 text-sm bg-white/20 backdrop-blur-sm text-white border-2 border-white/40 hover:bg-white/30 rounded-xl transition-all duration-200 font-semibold flex flex-col items-center gap-1 h-auto" 
                data-testid="btn-reclamacoes-desktop"
              >
                <Flag size={20} weight="duotone" />
                <span>{t('nav_reclamacoes')}</span>
              </Button>
              {pendingComplaintsCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[11px] font-bold rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center shadow-md border-2 border-white" data-testid="badge-reclamacoes-pendentes-desktop">
                  {pendingComplaintsCount}
                </span>
              )}
            </Link>
          </div>

          {/* Secondary Buttons Row - Catálogo Gresart + Documentos + Preços + Tabelas */}
          <div className="grid grid-cols-4 gap-2.5">
            <Link to="/catalogos" className="w-full">
              <Button
                className="w-full min-h-[68px] px-2 py-3 text-xs bg-white/20 backdrop-blur-sm text-white border-2 border-white/40 hover:bg-white/30 rounded-xl transition-all duration-200 font-semibold flex flex-col items-center justify-center gap-1 h-auto" 
                data-testid="btn-catalog-gresart-desktop"
              >
                <FilePdf size={20} weight="duotone" className="flex-shrink-0" />
                <span className="text-center leading-tight">{t('dashboard_gresart_catalog')}</span>
              </Button>
            </Link>
            <Link to="/documents" className="w-full">
              <Button 
                className="w-full min-h-[68px] px-2 py-3 text-xs bg-white/20 backdrop-blur-sm text-white border-2 border-white/40 hover:bg-white/30 rounded-xl transition-all duration-200 font-semibold flex flex-col items-center justify-center gap-1 h-auto" 
                data-testid="btn-view-documents-desktop"
              >
                <Files size={20} weight="duotone" className="flex-shrink-0" />
                <span className="text-center leading-tight">{t('nav_catalog_merch')}</span>
              </Button>
            </Link>
            <Link to="/precos" className="w-full">
              <Button 
                className="w-full min-h-[68px] px-2 py-3 text-xs bg-white/20 backdrop-blur-sm text-white border-2 border-white/40 hover:bg-white/30 rounded-xl transition-all duration-200 font-semibold flex flex-col items-center justify-center gap-1 h-auto" 
                data-testid="btn-precos-desktop"
              >
                <Tag size={20} weight="duotone" className="flex-shrink-0" />
                <span className="text-center leading-tight">{t('nav_precos')}</span>
              </Button>
            </Link>
            <Link to="/tabelas" className="w-full">
              <Button 
                className="w-full min-h-[68px] px-2 py-3 text-xs bg-white/20 backdrop-blur-sm text-white border-2 border-white/40 hover:bg-white/30 rounded-xl transition-all duration-200 font-semibold flex flex-col items-center justify-center gap-1 h-auto" 
                data-testid="btn-tabelas-desktop"
              >
                <Table size={20} weight="duotone" className="flex-shrink-0" />
                <span className="text-center leading-tight">{t('nav_tabelas')}</span>
              </Button>
            </Link>
          </div>


          {/* AI Tools Row - Sales Analysis + PPM Statistics + Encomendas Pendentes + Website */}
          <div className="grid grid-cols-4 gap-2.5">
            <Link to="/sales-analysis" className="w-full">
              <Button 
                className="w-full min-h-[68px] px-3 py-3 text-sm bg-white/20 backdrop-blur-sm text-white border-2 border-white/40 hover:bg-white/30 rounded-xl transition-all duration-200 font-semibold flex items-center justify-center gap-1.5 h-auto" 
                data-testid="btn-sales-analysis-desktop"
              >
                <ChartLineUp size={20} weight="duotone" className="flex-shrink-0" />
                <span>{t('dashboard_sales_analysis')}</span>
              </Button>
            </Link>
            <Link to="/statistics" className="w-full">
              <Button 
                className="w-full min-h-[68px] px-3 py-3 text-sm bg-white/20 backdrop-blur-sm text-white border-2 border-white/40 hover:bg-white/30 rounded-xl transition-all duration-200 font-semibold flex items-center justify-center gap-1.5 h-auto" 
                data-testid="btn-statistics-desktop"
              >
                <ChartBar size={20} weight="duotone" className="flex-shrink-0" />
                <span>{t('dashboard_statistics_ppm')}</span>
              </Button>
            </Link>
            <Link to="/encomendas" className="w-full">
              <Button 
                className="w-full min-h-[68px] px-3 py-3 text-sm bg-white/20 backdrop-blur-sm text-white border-2 border-white/40 hover:bg-white/30 rounded-xl transition-all duration-200 font-semibold flex items-center justify-center gap-1.5 h-auto" 
                data-testid="btn-encomendas-desktop"
              >
                <Truck size={20} weight="duotone" className="flex-shrink-0" />
                <span>Encomendas Pendentes</span>
              </Button>
            </Link>
            <Button 
              onClick={() => window.open('https://www.gresart.com', '_blank')}
              className="w-full min-h-[68px] px-3 py-3 text-sm bg-white/20 backdrop-blur-sm text-white border-2 border-white/40 hover:bg-white/30 rounded-xl transition-all duration-200 font-semibold flex items-center justify-center gap-1.5 h-auto" 
              data-testid="btn-website-gresart-desktop"
            >
              <Globe size={20} weight="duotone" className="flex-shrink-0" />
              <span>gresart.com</span>
            </Button>
          </div>
          </div>
        </div>

        {/* Decorative overlay pattern */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      {/* AI Assistant Mascot - bottom-right corner, "standing" on the hero base.
          Clicking opens the ChatbotWidget dialog (same event SalesAnalysis.jsx uses).
          Mobile: shrunk + fully below the hero edge so it never covers the button grid. */}
      <button
        onClick={() => window.dispatchEvent(new CustomEvent('openChatbot'))}
        className="absolute z-30 right-3 sm:right-6 md:right-8 -bottom-8 sm:-bottom-4 md:-bottom-5 transition-transform duration-300 hover:scale-110 focus:outline-none group"
        data-testid="btn-mascot-chatbot"
        aria-label="Abrir assistente IA"
      >
        <img
          src="/mascot-full-body.png?v=13"
          alt="Assistente IA Gresart"
          className="h-10 sm:h-20 md:h-24 lg:h-28 w-auto drop-shadow-xl transition-all duration-300 group-hover:drop-shadow-[0_0_18px_rgba(0,179,176,0.7)]"
        />
      </button>
      </div>
    </div>
  );
}
